import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import AppButton from "../button/AppButton";
import AppTextInput from "../inputs/AppTextInput";
import { CreateCleanerRequest } from "../../models/RequestModels/CreateCleanerRequest";
import { useTranslation } from "../../translations/i18n";
import AppPhoneInput from "../inputs/AppPhoneInput";

type TeamMemberRole = "manager" | "cleaner" | "viewer";

interface CleanerFormModalProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSubmit: (body: CreateCleanerRequest) => void;
}

const CleanerFormModal = ({
  open,
  loading,
  onClose,
  onSubmit,
}: CleanerFormModalProps) => {
  const { t } = useTranslation();

  const [role, setRole] = useState<TeamMemberRole>("cleaner");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  const resetForm = () => {
    setRole("cleaner");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setPassword("");
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (!firstName.trim() || !email.trim() || !password.trim()) {
      return;
    }

    onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim() || null,
      email: email.trim(),
      phone: phone.trim() || null,
      password,
      role,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {t("cleaners.newCleaner")}
      </DialogTitle>

      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>{t("roles.role")}</InputLabel>
          <Select
            label={t("roles.role")}
            value={role}
            disabled={loading}
            onChange={(event) => setRole(event.target.value as TeamMemberRole)}
          >
            <MenuItem value="manager">{t("roles.manager")}</MenuItem>
            <MenuItem value="cleaner">{t("roles.cleaner")}</MenuItem>
            <MenuItem value="viewer">{t("roles.viewer")}</MenuItem>
          </Select>
        </FormControl>

        <AppTextInput
          label={t("profile.firstName")}
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          placeholder={t("profile.firstNamePlaceholder")}
          disabled={loading}
          required
          autoFocus
        />

        <AppTextInput
          label={t("profile.lastName")}
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          placeholder={t("profile.lastNamePlaceholder")}
          disabled={loading}
        />

        <AppTextInput
          label={t("profile.email")}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t("profile.emailPlaceholder")}
          disabled={loading}
          required
          type="email"
        />

        <AppPhoneInput
          label={t("profile.phone")}
          value={phone}
          onChange={setPhone}
          placeholder={t("profile.phonePlaceholder")}
          disabled={loading}
        />

        <AppTextInput
          label={t("profile.password")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder={t("profile.newPasswordPlaceholder")}
          disabled={loading}
          required
          type="password"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AppButton
          title={t("common.cancel")}
          variant="outlined"
          color="inherit"
          fullWidth={false}
          disabled={loading}
          onClick={handleClose}
        />

        <AppButton
          title={t("cleaners.createCleaner")}
          fullWidth={false}
          loading={loading}
          disabled={!firstName.trim() || !email.trim() || !password.trim()}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CleanerFormModal;
